import {
  Table,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { IDataTableProps } from '../types';

type UseDataTableProps<T> = {
  enableFilters: IDataTableProps<T>['enableFilters'];
  initialState: IDataTableProps<T>['initialState'];
  tableData: IDataTableProps<T>['data'];
  tableColumns: IDataTableProps<T>['columns'];
  state: IDataTableProps<T>['state'];
  enableMultiSort: IDataTableProps<T>['enableMultiSort'];
  shouldUseDefaultSorting: IDataTableProps<T>['shouldUseDefaultSorting'];
  onSortingChange: IDataTableProps<T>['onSortingChange'];
  onGlobalFilterChange: IDataTableProps<T>['onGlobalFilterChange'];
  onColumnVisibilityChange: IDataTableProps<T>['onColumnVisibilityChange'];
  onExpandedChange: NonNullable<IDataTableProps<T>['subRows']>['onExpandedChange'];
  getRowCanExpand: IDataTableProps<T>['getRowCanExpand'];
  manualSorting: IDataTableProps<T>['manualSorting'];
};

type UseDataTable = <T extends object>(props: UseDataTableProps<T>) => Table<T>;

export const useDataTable: UseDataTable = ({
  enableFilters,
  initialState,
  tableData,
  tableColumns,
  state,
  enableMultiSort,
  onSortingChange,
  onGlobalFilterChange,
  onColumnVisibilityChange,
  onExpandedChange,
  getRowCanExpand,
  shouldUseDefaultSorting,
  manualSorting,
}) => {
  const table = useReactTable({
    // perses globally augments @tanstack/react-table's FilterFns to require `fuzzy`
    // (perses Table/model/table-model.ts), which forces every useReactTable to define it.
    // This table doesn't use fuzzy filtering — provide a simple substring matcher to satisfy the type.
    filterFns: {
      fuzzy: (row, columnId, filterValue) =>
        String(row.getValue(columnId) ?? '')
          .toLowerCase()
          .includes(String(filterValue ?? '').toLowerCase()),
    },
    enableFilters,
    autoResetExpanded: false,
    enableMultiSort,
    initialState,
    data: tableData,
    columns: tableColumns,
    state,
    defaultColumn: { minSize: 1 },
    isMultiSortEvent: enableMultiSort ? () => enableMultiSort : undefined,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: shouldUseDefaultSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: row => (row as any).subRows,
    ...(onSortingChange && { onSortingChange }),
    ...(onGlobalFilterChange && { onGlobalFilterChange }),
    ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
    ...(onExpandedChange && { onExpandedChange }),
    columnResizeMode: 'onChange',
    debugTable: window.location.hostname.includes('local'),
    ...(manualSorting && { manualSorting: true }),
  });

  return table;
};
